<template>
  <div>
    <sdui-loader v-if="waitingForFetch" class="custom-height" />

    <div
      v-else-if="!localEvent"
      class="not-available-container d-flex flex-column align-items-center justify-content-center"
      data-test="event-not-available"
    >
      <sdui-icon variant="danger" class="icon-message-warning xl" />
      <p class="text-muted text-center pt-4">
        {{ $t('calendar.event_details.event_not_available_anymore') }}
      </p>
    </div>

    <div v-else>
      <!-- event title-->
      <h2 class="font-weight-normal mb-1 text-break" data-cy="event-details-modal-title">{{ name }}</h2>

      <!-- event dateRange-->
      <div class="small gray-300 font-weight-light">
        <p class="mb-0" :data-cy="`event-details-modal-date-caption0${eventDateRange.caption0.dataCy}`">
          {{ eventDateRange.caption0.caption }}
        </p>
        <p class="mb-0" :data-cy="`event-details-modal-date-caption1${eventDateRange.caption1.dataCy}`">
          {{ eventDateRange.caption1.caption }}
        </p>
        <p v-if="eventDateRange.caption2"
           class="mb-0"
           :data-cy="`event-details-modal-date-caption2${eventDateRange.caption2.dataCy}`"
        >
          {{ eventDateRange.caption2.caption }}
        </p>
      </div>

      <!-- event participants-->
      <SList class="mt-4">
        <SListItem :icon-text="userShort"
                   icon-custom-colors="blue-dark-bg"
                   class="event-author"
                   data-test="event-author"
        >
          <template #item-title>
            <p class="mb-0 small gray-300">{{ $t('calendar.event_details.created_by') }}</p>
          </template>
          <template #description>
            <p class="mb-0" data-cy="event-details-modal-created-by">{{ userDisplayName }}</p>
          </template>
          <template #right-action>
            <b-button
              v-if="!isEventAuthor && canStartConversation"
              class="float-right reset-button"
              data-cy="event-details-modal-contact-author"
              variant="link"
              @click="startConversationWithAuthor"
            >
              <span class="sr-only">{{ userDisplayName }}</span>
              {{ $t('calendar.event_details.contact_author') }}
            </b-button>
          </template>
        </SListItem>

        <SListItem :title="$t('calendar.event_creation.details.participants.title')">
          <template #description>
            <div class="nano text-muted mt-3">
              <!-- only the author is invited -->
              <p
                v-if="authorIsOnlyInvitee"
                class="mb-0 small font-weight-light gray-400"
                data-cy="event-details-modal-invitees-only-author"
              >
                {{ $t('calendar.event_creation.details.participants.label_only_me') }}
              </p>

              <!-- users and channels are invited -->
              <div v-else-if="userInvitees && channelInvitees" data-cy="event-details-modal-invitees-users-channels">
                <div class="participants-with-groups pl-2">
                  <p class="mb-0 nano gray-300">{{ $t('calendar.event_details.groups') }}</p>
                  <p class="mb-0 small font-weight-light gray-400">{{ channelInvitees }}</p>
                </div>

                <div class="mt-3 participants-with-groups pl-2">
                  <p class="mb-0 nano gray-300">{{ $t('calendar.event_details.persons') }}</p>
                  <p class="mb-0 small font-weight-light gray-400">{{ userInvitees }}</p>
                </div>
              </div>

              <!-- only users are invited -->
              <p
                v-else-if="userInvitees && !channelInvitees"
                data-cy="event-details-modal-invitees-users"
                class="mb-0 small font-weight-light gray-400"
              >
                {{ userInvitees }}
              </p>

              <!-- only channels are invited -->
              <p v-else
                 class="mb-0 small font-weight-light gray-400"
                 data-cy="event-details-modal-invitees-channels"
              >
                {{ channelInvitees }}
              </p>
            </div>
          </template>
        </SListItem>
      </SList>

      <!-- event description-->
      <h2 class="font-weight-normal mt-4 mb-2">{{ $t('calendar.event_creation.details.description.title') }}</h2>
      <p
        class="mb-0 small font-weight-light content inner text-break"
        :class="{ 'gray-300': !description }"
        :data-cy="`event-details-modal-description${!description ? '-empty' : ''}`"
        v-html="$sanitize(description_rendered)"
      />

      <component
        :is="isModal ? 'SModalPortalTo' : 'div'"
        v-if="canDelete || canUpdate"
        :class="isModal ? '' : 'container fixed-bottom pb-3'"
      >
        <update-and-edit-event
          :can-delete="canDelete"
          :can-update="canUpdate"
          @delete-event-button-clicked="showConfirmDeleteModalAndTrack"
          @edit-event-button-clicked="redirectToEditModal"
        />
      </component>
    </div>

    <!-- CONFIRM DELETION MODAL -->
    <SConfirmModal
      v-model="showConfirmDeleteModal"
      :title="$t('calendar.event_details.delete.title')"
      :ok-title="$t('calendar.event_details.delete.submit')"
      :cancel-title="$t('calendar.event_details.delete.cancel')"
      @ok="deleteEvent"
      @hide="showConfirmDeleteModal = false"
    >
      <template #description>
        <div v-if="showConfirmDeleteModal">
          <p>{{ $t('calendar.event_details.delete.description') }}</p>
          <p>{{ $t('calendar.event_details.delete.notify_participants_description') }}</p>

          <SListItem
            class="mt-4"
            :title="$t('calendar.event_details.delete.notify_participants')"
            :aria-pressed="shouldNotifyParticipants ? 'true' : 'false'"
            @click="shouldNotifyParticipants = !shouldNotifyParticipants"
          >
            <template #right-action>
              <SCheckbox
                v-model="shouldNotifyParticipants"
                name="shouldNotifyParticipants"
                :label-text="$t('calendar.event_details.delete.notify_participants')"
                checkbox-type="switch"
              />
            </template>
          </SListItem>
        </div>
      </template>
    </SConfirmModal>
  </div>
</template>

<script>
import { categories } from 'src/views/Channels/Calendar/calendarCategories.js'
import dateTimePicker from 'src/mixins/miscellaneous/dateTimePicker.js'
import calendarMixin from 'src/mixins/channels/calendar/calendarMixin.js'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import Channels from 'src/api/channels.js'
import { Channel } from 'src/store/models/index.js'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import UpdateAndEditEvent from 'src/components/calendar/event/details/UpdateAndEditEvent.vue'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import { CalendarEvent } from 'src/store/models/channel/index.js'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'EventDetails',
  components: { SModalPortalTo, UpdateAndEditEvent, SduiLoader, SCheckbox },
  mixins: [dateTimePicker, calendarMixin],
  props: {
    event: {
      type: Object,
      default: null,
    },

    eventId: {
      type: [String, Number],
      default: null,
    },

    notification: {
      type: Object,
      default: null,
    },

    isWaiting: {
      type: Boolean,
      default: false,
    },

    isModal: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    categories,
    localEvent: null,
    showConfirmDeleteModal: false,
    shouldNotifyParticipants: true,
    isDeleting: false,
  }),

  computed: {
    waitingForFetch() {
      return this.$wait.waiting('fetch-event') || this.isWaiting
    },

    targetEventAvailable() {
      // If a user clicks the push notification for an event but the event is not available anymore, we can't
      // fetch the event details and display "event not available anymore".
      if (!this.notification) return true
      return !!this.notification?.target
    },

    eventDateRange() {
      const formattedDateCaptions = this.getFormattedDateCaptions(this.localEvent)
      if (!formattedDateCaptions) {
        return {
          caption0: {},
          caption1: {},
          caption2: {},
        }
      }
      return formattedDateCaptions
    },

    user() {
      return this.localEvent?.user
    },

    userShort() {
      const displayName = this.userDisplayName
      if (!displayName) return ''
      const namesArr = displayName.split(' ')
      const firstChar = namesArr[0].charAt(0)

      if (namesArr.length < 2) return firstChar.toUpperCase()
      return (firstChar + namesArr[namesArr.length - 1].charAt(0)).toUpperCase()
    },

    userDisplayName() {
      return this.user?.meta.displayname
    },

    category() {
      if (!this.localEvent || !this.localEvent?.category) return
      return this.$t(categories[this.localEvent.category]?.value)
    },

    name() {
      return this.localEvent?.name
    },

    channelInvitees() {
      return this.getChannelParticipants(this.localEvent)
        ?.map((channel) => channel.meta?.displayname)
        .join(', ')
    },

    userInvitees() {
      return this.getUserParticipants(this.localEvent)
        ?.map((channel) => channel.meta?.displayname)
        .join(', ')
    },

    authorIsOnlyInvitee() {
      return !this.localEvent?.channels?.length
    },

    description() {
      return this.localEvent?.description
    },

    description_rendered() {
      return this.localEvent?.description_rendered || this.$t('calendar.event_details.no_description')
    },

    canDelete() {
      return this.$can('delete', this.localEvent)
    },

    canUpdate() {
      return this.$can('update', this.localEvent)
    },

    // if user can delete and update, its the author
    isEventAuthor() {
      return this.canDelete && this.canUpdate
    },

    canStartConversation() {
      return this.$can('create-conversation', this.$me)
    },
  },

  emits: ['post-delete-event', 'category-changed', 'openEventCreation', 'close'],

  methods: {
    async startConversationWithAuthor() {
      clyTracking.trackCalendar().interaction.contactCreator()
      const res = await Channels.postChannelsStartConversation({ user_id: this.localEvent?.user.id })
      await Channel.insertOrUpdate({ data: res.data })
      await this.$router.push({ name: 'channel-single-chat', hash: '#read', params: { id: res.data.id } })
    },

    navigate(route, event) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          [route]: true,
          id: event.id,
        },
      })
    },

    async deleteEvent() {
      try {
        await this.sendDeleteEvent()
        clyTracking.trackCalendar().end.delete({})
        this.$emit('post-delete-event')
        this.$emit('close')
      } catch (e) {
        clyTracking.trackCalendar('calendarEvent', 'FAILED', e.message).end.delete({})
        throw e
      }
    },

    sendDeleteEvent: waitFor('delete-event', async function () {
      try {
        this.isDeleting = true
        await CalendarEvent.$delete(this.localEvent.id, {
          is_notifiable: this.shouldNotifyParticipants,
        })
      } finally {
        this.isDeleting = false
      }
    }),

    showConfirmDeleteModalAndTrack() {
      this.showConfirmDeleteModal = true
      clyTracking.trackCalendar().start.delete()
    },

    redirectToEditModal() {
      this.$store.commit('calendar/SET_EVENT', this.$clone(this.localEvent))
      this.$emit('openEventCreation')
      clyTracking.trackCalendar().start.edit()
      this.navigate('event-creation', this.localEvent)
    },
  },

  watch: {
    event: {
      handler: function (val) {
        this.localEvent = this.$clone(val)
        this.$emit('category-changed', this.category)
      },

      immediate: true,
    },

    eventId: {
      handler: async function (val) {
        if (!val || !this.targetEventAvailable) return
        await this.fetchEventById(val)
        this.$emit('category-changed', this.category)
      },

      immediate: true,
    },

    // re-fetch event details if it was edited to display updated details
    eventsHaveBeenChanged: {
      handler: async function (newVal) {
        if (!newVal || this.isDeleting || !this.localEvent.id) return
        await this.fetchEventById(this.localEvent.id)
        this.$emit('category-changed', this.category)
      },
    },
  },

  unmounted() {
    this.currentAbortController?.abort()
  }
}
</script>

<style scoped lang="scss">
.participants-with-groups {
  border-left: 2px solid $color-gray-100;
}

.custom-height {
  height: 50px;
}

.not-available-container {
  height: 50vh;
}

.event-author :deep(.sdui-list-item__icon-text) {
  font-weight: $font-weight-bold;
  font-size: pxtorem(16);
}
</style>
